import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "react-hot-toast";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../useAuthStore";

const useServiceStore = create(
  persist((set, get) => ({
    isLoading: false,
    allServices: [],
    builderServices: [],

    fetchServices: async () => {
      set({ isLoading: true });
      const { token } = useAuthStore.getState();
      try {
        const response = await axiosInstancev1.get("builder/default-services", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = response.data?.services || response.data || [];
        set({ allServices: list, isLoading: false });
      } catch (error) {
        set({ isLoading: false });
        toast.error(
          error.response?.data?.message || "Failed to fetch default services"
        );
      }
    },

    fetchBuilderService: async () => {
      set({ isLoading: true });
      const { token, user } = useAuthStore.getState(); // Get the full user object

      if (!user || !user.id) { // Check for user.id
        set({ isLoading: false });
        toast.error("Builder ID not found");
        return;
      }

      try {
        const response = await axiosInstancev1.get(
          `builder/${user.id}/services`, // Use user.id
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        set({ builderServices: response.data, isLoading: false });
      } catch (error) {
        set({ isLoading: false });
        toast.error(
          error.response?.data?.message || "Failed to fetch builder services"
        );
      }
    },

    fetchCreateService: async (name) => {
      // This creates a new general service, doesn't need builderId in path
      set({ isLoading: true });
      const { token } = useAuthStore.getState();
      try {
        const response = await axiosInstancev1.post(
          "builder/service",
          name,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        set((state) => ({
          allServices: [...state.allServices, response.data],
          isLoading: false,
        }));
        toast.success("Service added");
      } catch (error) {
        set({ isLoading: false });
        toast.error(
          error.response?.data?.message || "Failed to create service"
        );
      }
    },

    addServiceToBuilder: async (serviceId) => { // Removed builderId param as it's from auth store
      set({ isLoading: true });
      const { token, user } = useAuthStore.getState(); // Get the full user object

      if (!user || !user.id) { // Check for user.id
        set({ isLoading: false });
        toast.error("Builder ID not found. Cannot add service.");
        return;
      }

      try {
        await axiosInstancev1.post(
          `builder/service/add`, // Path seems generic, uses builderId from payload
          { serviceId, builderId: user.id }, // Send builderId in payload
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        set({ isLoading: false });
        toast.success("Service added to builder successfully");
        // Refresh builder-specific services after adding
        await get().fetchBuilderService();
      } catch (error) {
        set({ isLoading: false });
        toast.error(error.response?.data?.message || "Failed to add service");
      }
    },

    deleteService: async (serviceId) => {
      // This deletes a service (likely from the builder's list, API might need builderId too)
      // Assuming the API for deleting a service linked to a builder requires builderId implicitly via auth or in payload.
      // If it needs builderId in path like `/builder/{builderId}/service/{serviceId}`, adjust accordingly.
      // For now, assuming it's just `/builder/service/{serviceId}` and relies on token.
      set({ isLoading: true });
      const { token, user } = useAuthStore.getState(); // Get user to potentially pass builderId if needed by API

      // If your API for deleting a service from a builder needs builderId:
      // const payload = { builderId: user.id }; // Example if sent in payload

      try {
        await axiosInstancev1.delete(`builder/service/${serviceId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          // data: payload, // If builderId needs to be in payload for delete
        });
        // Optimistically remove from allServices AND builderServices if it was there
        set((state) => ({
          allServices: state.allServices.filter(service => service.id !== serviceId),
          builderServices: state.builderServices.filter(service => service.id !== serviceId),
          isLoading: false,
        }));
        toast.success("Service removed successfully");
      } catch (error) {
        set({ isLoading: false });
        toast.error(
          error.response?.data?.message || "Failed to remove service"
        );
      }
    },
  }))
);
export default useServiceStore;